/**
 * SQL 수집 — 수집 → 클린징(선택) → 변조(선택) → 검증 → 저장·머지, 그리고 이력(docs/features/29-sql-collect.md).
 * 수집 결과(세트)는 여러 기능이 쓴다 — 지금은 SQL 품질 검사의 대상(「수집 세트」), 다음은 액세스 패턴 분석.
 *
 * 틀은 SQL 품질 검사와 같다(왼쪽 = 세트 목록 | 변조 기준, 오른쪽 = 단계 리본). LLM 을 부르지 않는다 — 클린징·변조는 기계가 하고
 * 사람이 검증 단계에서 고친다(고친 것은 다시 돌려도 남는다).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FiDatabase, FiPlus, FiSliders, FiDownload, FiSquare, FiUpload, FiEdit2, FiGitMerge, FiCheck, FiRotateCcw, FiX, FiPlay } from 'react-icons/fi';
import AppModal from '../common/AppModal';
import { useConfirm } from '../ConfirmDialog';
import { formatApiError } from '../../utils/formatters';
import { storedTime } from '../../utils/storedTime';
import * as api from '../../services/sqlCollect';
import * as guardrailApi from '../../services/guardrail';
import { guardExport } from '../../utils/guardrailGate';
import RedactRuleEditor, { AutoNames, RULE_KINDS } from './RedactRuleEditor';
import '../sqlReview/SqlReviewModal.css';
import '../sqlAuthor/SqlAuthorModal.css';
import '../sqlQuality/SqlQualityModal.css';
import './SqlCollectModal.css';
import memoWindow from '../common/memoWindow';
import WorkflowHead, { WorkflowRibbon } from '../common/WorkflowHead';

const TABS = ['collect', 'cleanse', 'redact', 'verify', 'save', 'history'];
const KINDS = ['select', 'insert', 'update', 'delete', 'merge'];
const SUPPORTED = ['oracle', 'tibero', 'postgresql'];
const VIEWS = ['kept', 'issues', 'excluded', 'dup', 'edited', 'all'];
const PAGE = 50;
const fmt = (v) => (v == null ? '-' : Number(v).toLocaleString('en-US'));
const emptyCollect = () => ({ source: 'pool', kinds: ['select'], schema: '', module: '', q: '', limit: 1000, minExec: 1, days: 7, awrOk: false,
  csvText: '', csvName: '', delimiter: 'comma', append: false, plans: false, planMax: 100, objects: true });
const cleanseFrom = (set) => {
  const o = (set && set.cleanse && set.cleanse.options) || {};
  return { dedup: o.dedup || 'literal', sysSchema: o.sysSchema !== false, dictOnly: o.dictOnly !== false, plsql: o.plsql !== false, minExec: o.minExec || 0,
    modules: (o.modules || []).join(', '), words: (o.words || []).join(', ') };
};
const redactFrom = (set) => {
  const r = (set && set.redact) || {};
  return { ruleId: r.ruleId || '', off: !!r.off, auto: r.auto || { schema: true, table: true, column: true, index: true, function: false, guess: true },
    prefix: r.prefix || { schema: 'SCH', table: 'TB', column: 'COL', index: 'IX', function: 'FN', guess: 'COL' },
    keepText: (r.keep || []).join(', '), rows: (r.mapping || []).map(m => ({ ...m, manual: m.origin === 'manual' })), q: '', kind: '' };
};
const parseList = (text) => [...new Set(String(text || '').split(/[\s,]+/).map(x => x.trim().toUpperCase()).filter(Boolean))];

function downloadText(text, name, type) {
  const blob = new Blob([text], { type });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

const SqlCollectModal = ({ open, onClose, connections, defaultConnectionId, onOpenInEditor, restore, onView }) => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const conns = (connections || []).filter(c => SUPPORTED.includes(c.dbType || 'oracle'));
  const [sets, setSets] = useState([]);
  const [q, setQ] = useState('');
  const [set, setSet] = useState(null);
  const [step, setStep] = useState('collect');
  const [side, setSide] = useState('sets');            // sets = 수집 세트, rules = 변조 기준
  const [ruleSel, setRuleSel] = useState(null);
  const [rules, setRules] = useState([]);
  const [newForm, setNewForm] = useState(null);
  const [mergePick, setMergePick] = useState(null);    // null = 고르지 않음, Set(id) = 머지할 세트 고르는 중
  const [mergeTitle, setMergeTitle] = useState('');
  const [renaming, setRenaming] = useState(null);
  const [busy, setBusy] = useState(null);
  const [col, setCol] = useState(emptyCollect);
  const [cl, setCl] = useState(() => cleanseFrom(null));
  const [rd, setRd] = useState(() => redactFrom(null));
  const [toRule, setToRule] = useState('');
  const fileRef = useRef(null);
  const tRef = useRef(t);
  tRef.current = t;

  const reload = useCallback(async () => {
    try { setSets((await api.listSets(q)).sets || []); } catch (err) { toast.error(formatApiError(err, tRef.current)); }
  }, [q]);
  const reloadRules = useCallback(async () => {
    try { setRules((await api.listRules()).rules || []); } catch (_e) { setRules([]); }
  }, []);
  useEffect(() => { if (open) { reload(); reloadRules(); } }, [open, reload, reloadRules]);

  const adopt = (s) => { setSet(s); setCl(cleanseFrom(s)); setRd(redactFrom(s)); };
  const openSet = async (id, wantStep) => {
    try {
      const r = await api.getSet(id);
      adopt(r.set); setNewForm(null); setRenaming(null); setCol({ ...emptyCollect(), source: r.set.connectionId ? 'pool' : 'csv' });
      setStep(wantStep && TABS.includes(wantStep) ? wantStep : (r.set.summary && r.set.summary.items ? 'verify' : 'collect'));
    } catch (err) { toast.error(formatApiError(err, t)); }
  };

  // 새로고침 뒤 — 보던 세트·단계로 한 번만
  const [viewReady, setViewReady] = useState(false);
  useEffect(() => {
    if (!open || viewReady) return;
    if (!(restore && restore.id)) { setViewReady(true); return; }
    openSet(restore.id, restore.step).finally(() => setViewReady(true));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- 처음 열릴 때 한 번
  }, [open]);
  const viewId = set ? set.id : null;
  useEffect(() => { if (open && viewReady && onView) onView({ id: viewId, step }); }, [open, viewReady, viewId, step, onView]);

  const startNew = () => {
    setSide('sets'); setSet(null); setMergePick(null);
    setNewForm({ title: '', connectionId: (defaultConnectionId && conns.some(c => c.id === defaultConnectionId)) ? defaultConnectionId : '' });
  };
  const create = async () => {
    setBusy('create');
    try {
      const r = await api.createSet({ title: newForm.title, connectionId: newForm.connectionId || null });
      adopt(r.set); setNewForm(null); setStep('collect'); setCol({ ...emptyCollect(), source: r.set.connectionId ? 'pool' : 'csv' }); reload();
    } catch (err) { toast.error(formatApiError(err, t)); } finally { setBusy(null); }
  };
  const removeSet = async () => {
    const ok = await confirm({ title: t('common:scol.setDeleteTitle'), message: t('common:scol.setDeleteBody', { title: set.title }), confirmText: t('common:ui.delete'), variant: 'danger' });
    if (!ok) return;
    try { await api.deleteSet(set.id); setSet(null); reload(); } catch (err) { toast.error(formatApiError(err, t)); }
  };
  const rename = async () => {
    const title = String(renaming || '').trim();
    setRenaming(null);
    if (!title || title === set.title) return;
    try { const r = await api.renameSet(set.id, title); setSet(r.set); reload(); } catch (err) { toast.error(formatApiError(err, t)); }
  };
  const doMerge = async (ids) => {
    setBusy('merge');
    try {
      const r = await api.merge(ids, mergeTitle);
      setMergePick(null); setMergeTitle(''); await reload(); adopt(r.set); setStep('verify');
      toast.success(t('common:scol.merged', { count: r.set.summary ? r.set.summary.kept : 0 }));
    } catch (err) { toast.error(formatApiError(err, t)); } finally { setBusy(null); }
  };

  // ── 1) 수집 ─────────────────────────────────────────────
  const readCsv = (file) => {
    if (!file) return;
    const fr = new FileReader();
    fr.onload = () => setCol(c => ({ ...c, csvText: String(fr.result || ''), csvName: file.name }));
    fr.readAsText(file);
  };
  const runCollect = async () => {
    if (!col.append && set.summary && set.summary.items) {
      const ok = await confirm({ title: t('common:scol.replaceTitle'), message: t('common:scol.replaceBody', { n: set.summary.items }), confirmText: t('common:scol.replace'), variant: 'danger' });
      if (!ok) return;
    }
    setBusy('collect');
    try {
      const r = await api.collect(set.id, col);
      adopt(r.set); reload();
      const last = r.set.runs[r.set.runs.length - 1];
      toast.success(t('common:scol.collected', { count: last ? last.count : 0 }));
      if (fileRef.current) fileRef.current.value = '';
    } catch (err) { toast.error(formatApiError(err, t)); } finally { setBusy(null); }
  };
  const renderCollect = () => {
    const c = col;
    const setF = (k) => (e) => setCol({ ...c, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
    const isPg = set.dbType === 'postgresql';
    const runs = set.runs || [];
    return (
      <div className="stn-step">
        {runs.length > 0 && (
          <div className="stn-box sqq-fade">
            <b>{t('common:scol.runsTitle', { n: runs.length })}</b>
            <div className="stn-table-wrap">
              <table className="stn-table">
                <thead><tr><th>#</th><th>{t('common:scol.source')}</th><th className="num">{t('common:scol.count')}</th><th>{t('common:scol.at')}</th><th>{t('common:scol.notes')}</th></tr></thead>
                <tbody>{runs.map(r => (
                  <tr key={`${r.no}-${r.at}`}><td>{r.no}</td>
                    <td>{r.source === 'merge' ? t('common:scol.fromSet', { title: r.from }) : t(`common:scol.src.${r.source}`)}{r.csvName ? ` · ${r.csvName}` : ''}{r.schema ? ` · ${r.schema}` : ''}{r.plans ? ` · ${t('common:scol.withPlansShort')}` : ''}</td>
                    <td className="num">{fmt(r.count)}</td><td className="mono">{storedTime(r.at, 'min')}</td>
                    <td className="stn-small">{(r.notes || []).map(n => t(`common:scol.note.${n.split(':')[0]}`, { defaultValue: n, n: n.split(':')[1] })).join(' · ')}</td></tr>
                ))}</tbody>
              </table>
            </div>
          </div>
        )}
        <div className="stn-row sau-mode" role="radiogroup" aria-label={t('common:scol.source')}>
          {['pool', 'awr', 'csv'].map((s) => {
            const off = (s !== 'csv' && !set.connectionId) || (s === 'awr' && isPg);
            return (
              <label key={s} className={`sau-mode-opt${c.source === s ? ' on' : ''}${off ? ' sqq-disabled' : ''}`}>
                <input type="radio" name="scol-src" checked={c.source === s} disabled={off} onChange={() => setCol({ ...c, source: s })} />
                <b>{t(`common:scol.src.${s}`)}</b><span className="stn-note">{t(isPg && s === 'pool' ? 'common:scol.srcHintPgPool' : `common:scol.srcHint.${s}`)}</span>
              </label>
            );
          })}
        </div>
        {!set.connectionId && <span className="stn-note">{t('common:scol.noConnHint')}</span>}
        {c.source !== 'csv' && (
          <div className="stn-box sqq-fade" key="db">
            <b>{t('common:scol.rangeTitle')}</b>
            <div className="stn-row">
              <span className="stn-note">{t('common:scol.kinds')}</span>
              {KINDS.map(k => <label key={k} className="stn-row"><input type="checkbox" checked={c.kinds.includes(k)} onChange={e => setCol({ ...c, kinds: e.target.checked ? [...c.kinds, k] : c.kinds.filter(x => x !== k) })} /> {k.toUpperCase()}</label>)}
            </div>
            <div className="stn-grid2">
              <label className="stn-field"><span>{t('common:scol.schema')}</span><input value={c.schema} onChange={setF('schema')} placeholder={t('common:scol.schemaPh')} /></label>
              <label className="stn-field"><span>{t('common:scol.module')}</span><input value={c.module} onChange={setF('module')} placeholder={t('common:scol.modulePh')} /></label>
              <label className="stn-field"><span>{t('common:scol.textFilter')}</span><input value={c.q} onChange={setF('q')} placeholder={t('common:scol.textFilterPh')} /></label>
              <span className="stn-row">
                <label className="stn-field"><span>{t('common:scol.limit')}</span><input type="number" min="1" max="5000" className="sqq-num" value={c.limit} onChange={setF('limit')} /></label>
                <label className="stn-field"><span>{t('common:scol.minExec')}</span><input type="number" min="0" className="sqq-num" value={c.minExec} onChange={setF('minExec')} /></label>
                {c.source === 'awr' && <label className="stn-field"><span>{t('common:scol.days')}</span><input type="number" min="1" max="31" className="sqq-num" value={c.days} onChange={setF('days')} /></label>}
              </span>
            </div>
            <b>{t('common:scol.extraTitle')}</b>
            <label className="stn-row"><input type="checkbox" checked={c.objects} disabled={isPg} onChange={setF('objects')} /> {t('common:scol.withObjects')} <span className="stn-note">{t(isPg ? 'common:scol.oracleOnly' : 'common:scol.withObjectsHint')}</span></label>
            <span className="stn-row">
              <label className="stn-row"><input type="checkbox" checked={c.plans} disabled={isPg} onChange={setF('plans')} /> {t('common:scol.withPlans')}</label>
              <label className="stn-row stn-note">{t('common:scol.planMaxPre')}
                <input type="number" min="1" max="500" className="sqq-num" value={c.planMax} disabled={!c.plans || isPg} onChange={setF('planMax')} aria-label={t('common:scol.planMax')} />
                {t('common:scol.planMaxPost')}</label>
            </span>
            <span className="stn-note">{t(isPg ? 'common:scol.oracleOnly' : 'common:scol.withPlansHint')}</span>
            {c.source === 'awr' && <label className="stn-row sau-notice"><input type="checkbox" checked={c.awrOk} onChange={setF('awrOk')} /> {t('common:scol.awrLicense')}</label>}
            <span className="stn-note">{t('common:scol.dbMaskHint')}</span>
          </div>
        )}
        {c.source === 'csv' && (
          <div className="stn-box sqq-fade" key="csv">
            <div className="stn-row">
              <input ref={fileRef} type="file" accept=".csv,.txt,.tsv" style={{ display: 'none' }} onChange={e => readCsv(e.target.files && e.target.files[0])} />
              <button type="button" className="app-btn" onClick={() => fileRef.current && fileRef.current.click()}><FiUpload size={12} /> {t('common:scol.pickCsv')}</button>
              <span className="stn-note">{c.csvName ? t('common:scol.csvPicked', { name: c.csvName, kb: Math.ceil(c.csvText.length / 1024) }) : t('common:scol.csvNone')}</span>
              <select value={c.delimiter} onChange={setF('delimiter')} aria-label={t('common:scol.delimiter')}>
                {['comma', 'tab', 'semicolon', 'pipe'].map(d => <option key={d} value={d}>{t(`common:sqq.delim.${d}`)}</option>)}
              </select>
            </div>
            <span className="stn-note">{t('common:scol.csvHint')}</span>
          </div>
        )}
        {set.objects && (set.objects.tables || []).length > 0 && (
          <details className="stn-box">
            <summary><b>{t('common:scol.objectsTitle', { n: set.objects.tables.length })}</b> <span className="stn-note">{storedTime(set.objects.at, 'min')}</span></summary>
            <div className="stn-table-wrap">
              <table className="stn-table">
                <thead><tr><th>{t('common:scol.table')}</th><th className="num">{t('common:scol.rows')}</th><th>{t('common:scol.analyzed')}</th><th>{t('common:scol.indexes')}</th></tr></thead>
                <tbody>{set.objects.tables.slice(0, 300).map(tb => (
                  <tr key={`${tb.owner}.${tb.name}`}><td className="mono">{tb.owner}.{tb.name}</td><td className="num">{fmt(tb.rows)}</td><td className="mono">{tb.analyzed || '-'}</td>
                    <td className="stn-small mono">{(tb.indexes || []).map(x => `${x.name}${x.unique ? '(U)' : ''}: ${x.cols.join(',')}`).join(' / ')}</td></tr>
                ))}</tbody>
              </table>
            </div>
          </details>
        )}
        {busy === 'collect' && <div className="stn-progress">{t('common:scol.collecting')}</div>}
        <div className="stn-actions">
          {runs.length > 0 && <label className="stn-row" title={t('common:scol.appendHint')}><input type="checkbox" checked={c.append} onChange={setF('append')} /> {t('common:scol.append')}</label>}
          {busy === 'collect'
            ? <button type="button" className="app-btn app-btn-danger" onClick={() => api.cancelSet(set.id).catch(() => {})}><FiSquare size={12} /> {t('common:scol.stop')}</button>
            : <button type="button" className="app-btn app-btn-primary" onClick={runCollect}
              disabled={!!busy || (c.source === 'csv' ? !c.csvText : (!c.kinds.length || (c.source === 'awr' && !c.awrOk)))}><FiPlay size={12} /> {t('common:scol.runCollect')}</button>}
          {runs.length > 0 && <button type="button" className="app-btn" onClick={() => setStep('cleanse')}>{t('common:scol.next')}</button>}
        </div>
      </div>
    );
  };

  // ── 2) 클린징 ───────────────────────────────────────────
  const runCleanse = async () => {
    setBusy('cleanse');
    try {
      const r = await api.cleanse(set.id, { ...cl, modules: cl.modules, words: cl.words });
      adopt(r.set); reload();
      toast.success(t('common:scol.cleansed', { total: r.set.cleanse.counts.total, kept: r.set.cleanse.counts.kept }));
    } catch (err) { toast.error(formatApiError(err, t)); } finally { setBusy(null); }
  };
  const renderCleanse = () => {
    const c = set.cleanse;
    const setF = (k) => (e) => setCl({ ...cl, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
    return (
      <div className="stn-step">
        {c && (
          <div className="stn-box sqq-fade">
            <b>{t('common:scol.cleanseResult')}</b> {c.stale && <span className="stn-warn">{t('common:scol.stale')}</span>}
            <div className="sqq-kpis">
              {[['total', c.counts.total], ['kept', c.counts.kept], ['dup', c.counts.dup], ['excluded', c.counts.excluded]].map(([k, v]) => (
                <div key={k} className="sqq-kpi"><span className="stn-note">{t(`common:scol.kpi.${k}`)}</span><b>{fmt(v)}</b></div>
              ))}
            </div>
            {Object.keys(c.counts.byReason || {}).length > 0 && (
              <span className="stn-note">{Object.entries(c.counts.byReason).map(([k, v]) => `${t(`common:scol.reason.${k}`, { defaultValue: k })} ${fmt(v)}`).join(' · ')}</span>
            )}
          </div>
        )}
        <div className="stn-box">
          <b>{t('common:scol.dedupTitle')}</b>
          <div className="stn-row sau-mode" role="radiogroup" aria-label={t('common:scol.dedupTitle')}>
            {['literal', 'exact', 'none'].map(d => (
              <label key={d} className={`sau-mode-opt${cl.dedup === d ? ' on' : ''}`}>
                <input type="radio" name="scol-dedup" checked={cl.dedup === d} onChange={() => setCl({ ...cl, dedup: d })} />
                <b>{t(`common:scol.dedup.${d}`)}</b><span className="stn-note">{t(`common:scol.dedupHint.${d}`)}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="stn-box">
          <b>{t('common:scol.excludeTitle')}</b>
          {['sysSchema', 'dictOnly', 'plsql'].map(k => (
            <label key={k} className="stn-row"><input type="checkbox" checked={cl[k]} onChange={setF(k)} /> {t(`common:scol.ex.${k}`)}</label>
          ))}
          <label className="stn-row">{t('common:scol.ex.minExec')} <input type="number" min="0" className="sqq-num" value={cl.minExec} onChange={setF('minExec')} /></label>
          <div className="stn-grid2">
            <label className="stn-field"><span>{t('common:scol.ex.modules')}</span><input value={cl.modules} onChange={setF('modules')} placeholder={t('common:scol.ex.modulesPh')} /></label>
            <label className="stn-field"><span>{t('common:scol.ex.words')}</span><input value={cl.words} onChange={setF('words')} placeholder={t('common:scol.ex.wordsPh')} /></label>
          </div>
          <span className="stn-note">{t('common:scol.excludeHint')}</span>
        </div>
        <div className="stn-actions">
          <button type="button" className="app-btn app-btn-primary" onClick={runCleanse} disabled={!!busy || !(set.summary && set.summary.items)}><FiPlay size={12} /> {t('common:scol.runCleanse')}</button>
          <button type="button" className="app-btn" onClick={() => setStep('redact')}>{t(c ? 'common:scol.next' : 'common:scol.skip')}</button>
        </div>
      </div>
    );
  };

  // ── 3) 변조 ─────────────────────────────────────────────
  const runRedact = async (off = false) => {
    setBusy('redact');
    try {
      const r = await api.redact(set.id, off ? { off: true } : {
        ruleId: rd.ruleId || null, auto: rd.auto, prefix: rd.prefix, keep: parseList(rd.keepText),
        mapping: rd.rows.map(m => ({ from: m.from, to: m.to, kind: m.kind, manual: !!m.manual })),
      });
      adopt(r.set); reload();
      if (off) toast.info(t('common:scol.redactOffDone'));
      else toast.success(t('common:scol.redacted', { mapped: r.set.redact.counts.mapped, issues: r.set.redact.counts.issues }));
    } catch (err) { toast.error(formatApiError(err, t)); } finally { setBusy(null); }
  };
  const keepName = (from) => setRd({ ...rd, rows: rd.rows.filter(x => x.from !== from), keepText: [...parseList(rd.keepText), from].join(', ') });
  const addUnmapped = (from) => {
    if (rd.rows.some(x => x.from === from)) return;
    setRd({ ...rd, rows: [{ from, to: '', kind: 'guess', origin: 'manual', manual: true }, ...rd.rows], q: from });
  };
  const saveToRule = async () => {
    try {
      const r = await api.mappingToRule(set.id, toRule === '__new' ? { name: `${set.title}` } : { ruleId: toRule });
      toast.success(t('common:scol.toRuleDone', { name: r.rule.name, n: r.added })); reloadRules(); setToRule('');
    } catch (err) { toast.error(formatApiError(err, t)); }
  };
  const downloadMapping = async () => {
    try {
      const { csv } = await api.mappingCsv(set.id);
      downloadText(csv, `sql-collect-${(set.title || set.id).replace(/[\\/:*?"<>|\s]+/g, '_').slice(0, 60)}-mapping.csv`, 'text/csv;charset=utf-8');
    } catch (err) { toast.error(formatApiError(err, t)); }
  };
  const renderRedact = () => {
    const r = set.redact;
    const s = rd.q.trim().toUpperCase();
    const rows = rd.rows.filter(m => (!rd.kind || m.kind === rd.kind) && (!s || m.from.includes(s) || String(m.to).toUpperCase().includes(s)));
    const setRow = (from, patch) => setRd({ ...rd, rows: rd.rows.map(x => (x.from === from ? { ...x, ...patch, manual: true, origin: 'manual' } : x)) });
    return (
      <div className="stn-step">
        {r && !r.off && r.counts && (
          <div className="stn-box sqq-fade">
            <b>{t('common:scol.redactResult')}</b> {r.stale && <span className="stn-warn">{t('common:scol.stale')}</span>}
            <div className="sqq-kpis">
              {[['names', r.counts.names], ['mapped', r.counts.mapped], ['unmapped', r.counts.unmapped], ['issues', r.counts.issues]].map(([k, v]) => (
                <div key={k} className="sqq-kpi"><span className="stn-note">{t(`common:scol.kpi.${k}`)}</span><b>{fmt(v)}</b></div>
              ))}
            </div>
            <span className="stn-note">{Object.entries(r.counts.byKind || {}).map(([k, v]) => `${t(`common:scol.kind.${k}`)} ${fmt(v)}`).join(' · ')}{r.ruleName ? ` · ${t('common:scol.usedRule', { name: r.ruleName })}` : ''}</span>
            {(r.unmapped || []).length > 0 && (
              <div className="scol-unmapped">
                <span className="stn-note">{t('common:scol.unmappedHint')}</span>
                <div className="sqq-chips">{r.unmapped.slice(0, 60).map(n => (
                  <button key={n} type="button" className="sqq-chip mono" onClick={() => addUnmapped(n)} title={t('common:scol.addUnmapped')}>{n}</button>
                ))}</div>
              </div>
            )}
          </div>
        )}
        {r && r.off && <div className="stn-box sau-notice">{t('common:scol.redactIsOff')}</div>}
        <div className="stn-grid2">
          <div className="stn-box">
            <b>{t('common:scol.ruleTitle')}</b>
            <select value={rd.ruleId} onChange={e => setRd({ ...rd, ruleId: e.target.value })} aria-label={t('common:scol.ruleTitle')}>
              <option value="">{t('common:scol.noRule')}</option>
              {rules.map(x => <option key={x.id} value={x.id}>{x.name} ({fmt(x.mappings)})</option>)}
            </select>
            <span className="stn-note">{t('common:scol.ruleUseHint')}</span>
            <b>{t('common:scol.keepTitle')}</b>
            <textarea className="mono" rows={3} value={rd.keepText} onChange={e => setRd({ ...rd, keepText: e.target.value })} placeholder={t('common:scol.keepPh')} aria-label={t('common:scol.keepTitle')} />
          </div>
          <div className="stn-box">
            <b>{t('common:scol.autoTitle')}</b>
            <AutoNames auto={rd.auto} prefix={rd.prefix} onChange={({ auto, prefix }) => setRd({ ...rd, auto, prefix })} />
          </div>
        </div>
        <div className="stn-box">
          <div className="stn-row stn-between">
            <b>{t('common:scol.mappingTitle', { n: rd.rows.length })}</b>
            <span className="stn-row">
              <select value={rd.kind} onChange={e => setRd({ ...rd, kind: e.target.value })} aria-label={t('common:scol.kindLabel')}>
                <option value="">{t('common:scol.allKinds')}</option>
                {RULE_KINDS.map(k => <option key={k} value={k}>{t(`common:scol.kind.${k}`)}</option>)}
              </select>
              <input value={rd.q} onChange={e => setRd({ ...rd, q: e.target.value })} placeholder={t('common:scol.searchMapping')} aria-label={t('common:scol.searchMapping')} />
            </span>
          </div>
          {!rd.rows.length && <span className="stn-note">{t('common:scol.mappingEmpty')}</span>}
          {rd.rows.length > 0 && (
            <div className="stn-table-wrap scol-map-wrap">
              <table className="stn-table">
                <thead><tr><th>{t('common:scol.from')}</th><th>{t('common:scol.to')}</th><th>{t('common:scol.kindLabel')}</th><th>{t('common:scol.origin')}</th><th aria-label={t('common:scol.keepOne')} /></tr></thead>
                <tbody>
                  {rows.slice(0, 400).map(m => (
                    <tr key={m.from}>
                      <td className="mono">{m.from}</td>
                      <td><input className="mono scol-to" value={m.to} aria-label={t('common:scol.toOf', { name: m.from })} onChange={e => setRow(m.from, { to: e.target.value })} /></td>
                      <td><select value={m.kind} aria-label={t('common:scol.kindLabel')} onChange={e => setRow(m.from, { kind: e.target.value })}>
                        {RULE_KINDS.map(k => <option key={k} value={k}>{t(`common:scol.kind.${k}`)}</option>)}
                      </select></td>
                      <td><span className={`sqq-tag scol-origin-${m.manual ? 'manual' : m.origin}`}>{t(`common:scol.originOf.${m.manual ? 'manual' : m.origin}`, { defaultValue: m.origin })}</span></td>
                      <td><button type="button" className="stn-icon-btn" title={t('common:scol.keepOne')} aria-label={t('common:scol.keepOne')} onClick={() => keepName(m.from)}><FiX size={12} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {rows.length > 400 && <span className="stn-note">{t('common:scol.mappingMore', { n: rows.length - 400 })}</span>}
          <span className="stn-note">{t('common:scol.mappingHint')}</span>
        </div>
        <div className="stn-actions">
          {r && !r.off && r.mapping && (
            <>
              <button type="button" className="app-btn" onClick={downloadMapping} title={t('common:scol.mappingCsvHint')}><FiDownload size={12} /> {t('common:scol.mappingCsv')}</button>
              <select value={toRule} onChange={e => setToRule(e.target.value)} aria-label={t('common:scol.toRule')}>
                <option value="">{t('common:scol.toRulePick')}</option>
                <option value="__new">{t('common:scol.toRuleNew')}</option>
                {rules.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
              </select>
              <button type="button" className="app-btn" onClick={saveToRule} disabled={!toRule}>{t('common:scol.toRule')}</button>
            </>
          )}
          <button type="button" className="app-btn" onClick={() => runRedact(true)} disabled={!!busy} title={t('common:scol.redactOffHint')}>{t('common:scol.redactOff')}</button>
          <button type="button" className="app-btn app-btn-primary" onClick={() => runRedact(false)} disabled={!!busy || !(set.summary && set.summary.items)}><FiPlay size={12} /> {t('common:scol.runRedact')}</button>
          <button type="button" className="app-btn" onClick={() => setStep('verify')}>{t('common:scol.next')}</button>
        </div>
      </div>
    );
  };

  // ── 4) 검증 ─────────────────────────────────────────────
  const [view, setView] = useState('kept');
  const [vq, setVq] = useState('');
  const [page, setPage] = useState(0);
  const [list, setList] = useState({ total: 0, items: [] });
  const [item, setItem] = useState(null);
  const [edit, setEdit] = useState(null);           // { cleanSql, redSql, note } — 고치는 중
  const [planSide, setPlanSide] = useState('red');
  const loadList = useCallback(async () => {
    if (!set) return;
    try { setList(await api.listItems(set.id, { view, q: vq, offset: page * PAGE, limit: PAGE })); } catch (err) { toast.error(formatApiError(err, tRef.current)); }
  }, [set, view, vq, page]);
  useEffect(() => { if (open && set && step === 'verify') loadList(); },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 세트·보기·쪽이 바뀔 때
    [open, step, set && set.id, view, page]);
  const pickItem = async (id) => {
    try { const r = await api.getItem(set.id, id); setItem(r.item); setEdit({ cleanSql: r.item.cleanSql || r.item.rawSql, redSql: r.item.redSql || '', note: r.item.note || '' }); } catch (err) { toast.error(formatApiError(err, t)); }
  };
  const itemDirty = !!(item && edit && (edit.cleanSql !== (item.cleanSql || item.rawSql) || (item.redSql != null && edit.redSql !== item.redSql) || edit.note !== (item.note || '')));
  const patchItem = async (patch) => {
    try {
      const r = await api.updateItem(set.id, item.id, patch);
      setItem(r.item); setEdit({ cleanSql: r.item.cleanSql || r.item.rawSql, redSql: r.item.redSql || '', note: r.item.note || '' });
      setSet(r.set); loadList();
    } catch (err) { toast.error(formatApiError(err, t)); }
  };
  const saveItem = () => {
    const p = {};
    if (edit.cleanSql !== (item.cleanSql || item.rawSql)) p.cleanSql = edit.cleanSql;
    if (item.redSql != null && edit.redSql !== item.redSql && p.cleanSql === undefined) p.redSql = edit.redSql;
    if (edit.note !== (item.note || '')) p.note = edit.note;
    return patchItem(p);
  };
  const renderVerify = () => {
    const sm = set.summary || {};
    const counts = { kept: sm.kept, issues: sm.issues, excluded: sm.excluded, dup: sm.dup, edited: sm.edited, all: sm.items };
    const pages = Math.max(1, Math.ceil(list.total / PAGE));
    return (
      <div className="stn-step">
        <div className="stn-row stn-between">
          <div className="sqq-chips scol-views" role="tablist" aria-label={t('common:scol.viewLabel')}>
            {VIEWS.map(v => (
              <button key={v} type="button" role="tab" aria-selected={view === v} className={`sqq-chip${view === v ? ' on' : ''}`} onClick={() => { setView(v); setPage(0); setItem(null); }}>
                {t(`common:scol.view.${v}`)} <span className="sqq-chip-n">{fmt(counts[v] || 0)}</span>
              </button>
            ))}
          </div>
          <input value={vq} onChange={e => setVq(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { setPage(0); loadList(); } }} placeholder={t('common:scol.searchItems')} aria-label={t('common:scol.searchItems')} />
        </div>
        <div className="sqq-split scol-split">
          <ul className="sqq-list">
            {list.items.map(it => (
              <li key={it.id} className={item && item.id === it.id ? 'on' : ''} onClick={() => pickItem(it.id)}>
                <span className="mono stn-note">{it.seq}</span>
                <div className="sqq-li-body">
                  <div className="stn-row">
                    {it.sqlId && <span className="mono stn-small">{it.sqlId}</span>}
                    {it.dupCount > 0 && <span className="sqq-tag">{t('common:scol.dupBadge', { n: it.dupCount })}</span>}
                    {it.excluded && <span className="sqq-tag scol-tag-ex">{t(`common:scol.reason.${it.excludeReason}`, { defaultValue: it.excludeReason })}</span>}
                    {(it.cleanEdited || it.redEdited) && <span className="sqq-tag scol-tag-edit">{t('common:scol.editedBadge')}</span>}
                    {(it.issues || []).length > 0 && <span className="sqq-li-checks">{t('common:scol.issuesBadge', { n: it.issues.length })}</span>}
                  </div>
                  <span className="sqq-li-head mono">{it.redHead || it.cleanHead}</span>
                </div>
              </li>
            ))}
            {!list.items.length && <li className="stn-empty">{t('common:scol.noItems')}</li>}
          </ul>
          <div className="sqq-detail">
            {!item && <div className="stn-note">{t('common:scol.pickItem')}</div>}
            {item && edit && (
              <div className="sqq-fade" key={item.id}>
                <div className="stn-row stn-between">
                  <span className="stn-row">
                    <b>#{item.seq}</b>{item.sqlId && <span className="mono">{item.sqlId}</span>}
                    <span className="stn-note">{[item.schema, item.module, t(`common:scol.src.${item.source}`), item.origin].filter(Boolean).join(' · ')}</span>
                  </span>
                  <span className="stn-row">
                    {item.excluded
                      ? <button type="button" className="app-btn" onClick={() => patchItem({ manualExclude: false })}><FiRotateCcw size={12} /> {t('common:scol.include')}</button>
                      : <button type="button" className="app-btn" onClick={() => patchItem({ manualExclude: true })} disabled={!!item.dupOf}><FiX size={12} /> {t('common:scol.exclude')}</button>}
                    {item.manualExclude != null && <button type="button" className="app-btn" onClick={() => patchItem({ manualExclude: null })} title={t('common:scol.autoExcludeHint')}>{t('common:scol.autoExclude')}</button>}
                  </span>
                </div>
                {item.dupOf && <span className="sau-notice">{t('common:scol.isDup')}</span>}
                {(item.cleanStats || item.stats) && (
                  <span className="stn-note">{t('common:scol.statsLine', { ex: fmt((item.cleanStats || item.stats).executions), avg: (item.cleanStats || item.stats).avgElapsedSec ?? '-', buf: fmt((item.cleanStats || item.stats).bufPerExec) })}</span>
                )}
                {(item.issues || []).length > 0 && (
                  <div className="scol-issues">{t('common:scol.issuesLine')} {item.issues.map(n => <code key={n} className="scol-issue">{n}</code>)}</div>
                )}
                <div className="scol-sqls">
                  <label className="stn-field">
                    <span className="stn-row stn-between">{t('common:scol.cleanSql')}
                      {item.cleanEdited && <button type="button" className="stn-icon-btn" onClick={() => patchItem({ reset: 'clean' })} title={t('common:scol.resetClean')} aria-label={t('common:scol.resetClean')}><FiRotateCcw size={12} /></button>}
                    </span>
                    <textarea className="mono scol-sql" rows={10} value={edit.cleanSql} onChange={e => setEdit({ ...edit, cleanSql: e.target.value })} />
                  </label>
                  {item.redSql != null && (
                    <label className="stn-field">
                      <span className="stn-row stn-between">{t('common:scol.redSql')}
                        {item.redEdited && <button type="button" className="stn-icon-btn" onClick={() => patchItem({ reset: 'red' })} title={t('common:scol.resetRed')} aria-label={t('common:scol.resetRed')}><FiRotateCcw size={12} /></button>}
                      </span>
                      <textarea className="mono scol-sql" rows={10} value={edit.redSql} disabled={edit.cleanSql !== (item.cleanSql || item.rawSql)}
                        onChange={e => setEdit({ ...edit, redSql: e.target.value })} />
                    </label>
                  )}
                </div>
                {item.cleanEdited && item.cleanSql !== item.rawSql && (
                  <details><summary className="stn-note">{t('common:scol.rawSql')}</summary><pre className="stn-pre mono sqq-sql">{item.rawSql}</pre></details>
                )}
                <label className="stn-field"><span>{t('common:scol.note')}</span>
                  <input value={edit.note} maxLength={1000} onChange={e => setEdit({ ...edit, note: e.target.value })} /></label>
                {item.planText && (
                  <div className="stn-box">
                    <div className="stn-row stn-between"><b>{t('common:scol.plan')}</b>
                      {item.redPlan && (
                        <span className="stn-row scol-planside">
                          {['red', 'clean'].map(p => <button key={p} type="button" className={`app-btn${planSide === p ? ' on' : ''}`} onClick={() => setPlanSide(p)}>{t(`common:scol.planSide.${p}`)}</button>)}
                        </span>
                      )}
                    </div>
                    <pre className="stn-pre mono scol-plan">{planSide === 'red' && item.redPlan ? item.redPlan : item.planText}</pre>
                  </div>
                )}
                {(item.dups || []).length > 0 && (
                  <div className="stn-box">
                    <b>{t('common:scol.dupsTitle', { n: item.dupCount })}</b>
                    <ul className="sqq-hist">{item.dups.map(d => <li key={d.id}><span className="mono">{d.sqlId || '-'}</span> <span className="stn-note">({fmt(d.executions)})</span> <span className="mono stn-small">{d.head}</span></li>)}</ul>
                  </div>
                )}
                {onOpenInEditor && (
                  <div className="stn-actions">
                    <button type="button" className="app-btn" onClick={() => onOpenInEditor(item.redSql != null && planSide === 'red' ? item.redSql : (item.cleanSql || item.rawSql))}>{t('common:scol.openInEditor')}</button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="stn-row stn-between">
          <span className="stn-note">{t('common:scol.pageLine', { total: fmt(list.total), page: page + 1, pages })}</span>
          <span className="stn-row">
            <button type="button" className="app-btn" disabled={page === 0} onClick={() => setPage(page - 1)}>{t('common:scol.prev')}</button>
            <button type="button" className="app-btn" disabled={page + 1 >= pages} onClick={() => setPage(page + 1)}>{t('common:scol.nextPage')}</button>
          </span>
        </div>
      </div>
    );
  };

  // ── 5) 저장 · 머지 ──────────────────────────────────────
  const [mergeWith, setMergeWith] = useState([]);
  const exportSet = async (which) => {
    try {
      const { csv } = await api.exportCsv(set.id, which);
      // 변조 전 글에는 원래 이름이 있다 — 내보내기는 가드레일 검사를 지난다(리포트 md 와 같은 길)
      const g = await guardExport(csv, { scan: guardrailApi.scanText, redact: guardrailApi.redactText });
      if (!g) return;
      if (g.mode === 'unchecked') toast.warning(t('common:grd.exportUnchecked'));
      downloadText(g.text, `sql-collect-${(set.title || set.id).replace(/[\\/:*?"<>|\s]+/g, '_').slice(0, 60)}-${which}.csv`, 'text/csv;charset=utf-8');
    } catch (err) { toast.error(formatApiError(err, t)); }
  };
  const done = async () => {
    try { const r = await api.finalize(set.id); setSet(r.set); reload(); toast.success(t('common:scol.doneToast')); } catch (err) { toast.error(formatApiError(err, t)); }
  };
  const renderSave = () => {
    const sm = set.summary || {};
    const redOn = set.redact && !set.redact.off && set.redact.mapping;
    const others = sets.filter(x => x.id !== set.id);
    return (
      <div className="stn-step">
        <div className="sqq-kpis scol-kpis">
          {[['items', sm.items], ['kept', sm.kept], ['dup', sm.dup], ['excluded', sm.excluded], ['redacted', sm.redacted], ['issues', sm.issues], ['edited', sm.edited], ['plans', sm.plans]].map(([k, v]) => (
            <div key={k} className="sqq-kpi"><span className="stn-note">{t(`common:scol.kpi.${k}`)}</span><b>{fmt(v || 0)}</b></div>
          ))}
        </div>
        {(set.cleanse && set.cleanse.stale) && <span className="stn-warn">{t('common:scol.staleCleanse')}</span>}
        {(set.redact && set.redact.stale) && <span className="stn-warn">{t('common:scol.staleRedact')}</span>}
        {redOn && sm.issues > 0 && <span className="sau-notice">{t('common:scol.issuesLeft', { n: sm.issues })}</span>}
        <div className="stn-box">
          <b>{t('common:scol.saveTitle')}</b>
          <span className="stn-note">{t('common:scol.saveHint')}</span>
          <div className="stn-row">
            {set.status === 'done'
              ? <span className="stn-row"><FiCheck size={12} /> {t('common:scol.doneAt', { at: storedTime(set.doneAt, 'min') })}</span>
              : <button type="button" className="app-btn app-btn-primary" onClick={done} disabled={!sm.kept}><FiCheck size={12} /> {t('common:scol.markDone')}</button>}
          </div>
          <span className="stn-note">{t('common:scol.usedBy')}</span>
        </div>
        <div className="stn-box">
          <b>{t('common:scol.exportTitle')}</b>
          <div className="stn-row">
            <button type="button" className="app-btn app-btn-primary" onClick={() => exportSet('red')} disabled={!redOn}><FiDownload size={12} /> {t('common:scol.exportRed')}</button>
            <button type="button" className="app-btn" onClick={() => exportSet('clean')}><FiDownload size={12} /> {t('common:scol.exportClean')}</button>
            <button type="button" className="app-btn" onClick={() => exportSet('both')} disabled={!redOn}><FiDownload size={12} /> {t('common:scol.exportBoth')}</button>
          </div>
          <span className="stn-note">{t('common:scol.exportHint')}</span>
        </div>
        <div className="stn-box">
          <b>{t('common:scol.mergeTitle')}</b>
          <span className="stn-note">{t('common:scol.mergeHint')}</span>
          {!others.length && <span className="stn-note">{t('common:scol.mergeNoOthers')}</span>}
          <div className="scol-merge-list">
            {others.map(x => (
              <label key={x.id} className="stn-row">
                <input type="checkbox" checked={mergeWith.includes(x.id)} onChange={e => setMergeWith(e.target.checked ? [...mergeWith, x.id] : mergeWith.filter(y => y !== x.id))} />
                {x.title} <span className="stn-note">· {fmt(x.items)} · {storedTime(x.updatedAt, 'date')}</span>
              </label>
            ))}
          </div>
          <div className="stn-row">
            <input value={mergeTitle} onChange={e => setMergeTitle(e.target.value)} placeholder={t('common:scol.mergeTitlePh')} aria-label={t('common:scol.mergeTitlePh')} />
            <button type="button" className="app-btn app-btn-primary" disabled={!mergeWith.length || !!busy} onClick={() => doMerge([set.id, ...mergeWith]).then(() => setMergeWith([]))}>
              <FiGitMerge size={12} /> {t('common:scol.mergeRun', { n: mergeWith.length + 1 })}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderHistory = () => (
    <div className="stn-step">
      {(set.mergedFrom || []).length > 0 && (
        <div className="stn-box"><b>{t('common:scol.mergedFrom')}</b>
          <ul className="sqq-hist">{set.mergedFrom.map(m => <li key={m.id}>{m.title} <span className="stn-note">({fmt(m.items)})</span></li>)}</ul>
        </div>
      )}
      <div className="stn-box">
        <b>{t('common:scol.historyTitle')}</b>
        <div className="stn-table-wrap">
          <table className="stn-table">
            <thead><tr><th>{t('common:scol.at')}</th><th>{t('common:scol.who')}</th><th>{t('common:scol.what')}</th><th>{t('common:scol.notes')}</th></tr></thead>
            <tbody>
              {[...(set.history || [])].reverse().map((h, i) => (
                // eslint-disable-next-line react/no-array-index-key -- 이력은 순서가 곧 키다
                <tr key={i}><td className="mono">{storedTime(h.at, 'min')}</td><td>{t(`common:scol.actor.${h.actor}`, { defaultValue: h.actor })}</td>
                  <td>{t(`common:scol.action.${h.action}`, { defaultValue: h.action })}</td><td className="stn-small">{h.note}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const body = !set ? null : { collect: renderCollect, cleanse: renderCleanse, redact: renderRedact, verify: renderVerify, save: renderSave, history: renderHistory }[step]();
  const sm = (set && set.summary) || {};

  return (
    <AppModal windowed open={open} onClose={onClose} title={t('menu:tools.sqlCollect')} icon={<FiDatabase size={14} />} className="app-modal-default stn-modal">
      <div className="stn-body">
        <aside className="stn-side">
          <div className="stn-row stn-report-mode sqq-side-switch" role="tablist" aria-label={t('common:scol.sideSwitch')}>
            {['sets', 'rules'].map(s => (
              <button key={s} type="button" role="tab" aria-selected={side === s} className={`app-btn${side === s ? ' on' : ''}`} onClick={() => setSide(s)}>
                {s === 'sets' ? <FiDatabase size={12} /> : <FiSliders size={12} />} {t(`common:scol.side.${s}`)}
              </button>
            ))}
          </div>
          {side === 'sets' ? (
            <>
              <button type="button" className="app-btn app-btn-primary stn-full" onClick={startNew}><FiPlus size={12} /> {t('common:scol.newSet')}</button>
              {mergePick
                ? (
                  <div className="scol-merge-bar sqq-fade">
                    <input value={mergeTitle} onChange={e => setMergeTitle(e.target.value)} placeholder={t('common:scol.mergeTitlePh')} aria-label={t('common:scol.mergeTitlePh')} />
                    <span className="stn-row">
                      <button type="button" className="app-btn app-btn-primary" disabled={mergePick.size < 2 || !!busy} onClick={() => doMerge([...mergePick])}><FiGitMerge size={12} /> {t('common:scol.mergeRun', { n: mergePick.size })}</button>
                      <button type="button" className="app-btn" onClick={() => setMergePick(null)}>{t('common:ui.cancel')}</button>
                    </span>
                  </div>
                )
                : <button type="button" className="app-btn stn-full" onClick={() => setMergePick(new Set())} disabled={sets.length < 2} title={t('common:scol.mergeHint')}><FiGitMerge size={12} /> {t('common:scol.mergePick')}</button>}
              <input className="stn-full" value={q} onChange={e => setQ(e.target.value)} placeholder={t('common:scol.setSearch')} onKeyDown={e => { if (e.key === 'Enter') reload(); }} />
              <ul className="stn-jobs sqq-fade" key="sets">
                {sets.map(x => (
                  <li key={x.id} className={set && set.id === x.id && !newForm ? 'on' : ''}
                    onClick={() => (mergePick ? setMergePick(p => { const n = new Set(p); if (n.has(x.id)) n.delete(x.id); else n.add(x.id); return n; }) : openSet(x.id))}>
                    <div className="stn-job-title">
                      {mergePick && <input type="checkbox" readOnly checked={mergePick.has(x.id)} aria-label={x.title} />} {x.title}
                    </div>
                    <div className="stn-job-meta">{t(`common:scol.status.${x.status}`, { defaultValue: x.status })} · {fmt(x.summary ? x.summary.kept : x.items)} / {fmt(x.items)} · {storedTime(x.updatedAt, 'date')}</div>
                  </li>
                ))}
                {!sets.length && <li className="stn-empty">{t('common:scol.noSets')}</li>}
              </ul>
            </>
          ) : (
            <>
              <button type="button" className="app-btn app-btn-primary stn-full" onClick={() => setRuleSel({ id: null, nonce: Date.now() })}><FiPlus size={12} /> {t('common:scol.newRule')}</button>
              <ul className="stn-jobs sqq-fade" key="rules">
                {rules.map(r => (
                  <li key={r.id} className={ruleSel && ruleSel.id === r.id ? 'on' : ''} onClick={() => setRuleSel({ id: r.id })}>
                    <div className="stn-job-title">{r.name}</div>
                    <div className="stn-job-meta">{t('common:scol.ruleMeta', { m: fmt(r.mappings), k: fmt(r.keeps) })} · {storedTime(r.updatedAt, 'date')}</div>
                  </li>
                ))}
                {!rules.length && <li className="stn-empty">{t('common:scol.noRules')}</li>}
              </ul>
            </>
          )}
        </aside>
        <section className="stn-main">
          {side === 'rules' && (
            <RedactRuleEditor selected={ruleSel}
              onSaved={(r) => { reloadRules(); setRuleSel({ id: r.id }); }}
              onDeleted={() => { reloadRules(); setRuleSel(null); }} />
          )}
          {side === 'sets' && <>
            {newForm && (
              <div className="stn-new sqq-fade">
                <label className="stn-field"><span>{t('common:scol.title')}</span>
                  <input value={newForm.title} onChange={e => setNewForm({ ...newForm, title: e.target.value })} placeholder={t('common:scol.titlePh')} /></label>
                <label className="stn-field"><span>{t('common:scol.connection')}</span>
                  <select value={newForm.connectionId} onChange={e => setNewForm({ ...newForm, connectionId: e.target.value })}>
                    <option value="">{t('common:scol.noConnection')}</option>
                    {conns.map(c => <option key={c.id} value={c.id}>{c.name || c.id}</option>)}
                  </select>
                </label>
                <span className="stn-note">{t('common:scol.newHint')}</span>
                <div className="stn-actions">
                  <button type="button" className="app-btn" onClick={() => setNewForm(null)}>{t('common:ui.cancel')}</button>
                  <button type="button" className="app-btn app-btn-primary" onClick={create} disabled={!!busy}><FiPlus size={12} /> {t('common:scol.create')}</button>
                </div>
              </div>
            )}
            {!newForm && !set && <div className="stn-empty-main">{t('common:scol.intro')}</div>}
            {!newForm && set && (
              <>
                <WorkflowHead
                  titleSlot={renaming != null ? (
                    <input className="stn-rename" value={renaming} maxLength={200} aria-label={t('common:scol.rename')} autoFocus
                      onChange={e => setRenaming(e.target.value)} onBlur={rename}
                      onKeyDown={e => { if (e.key === 'Enter') rename(); else if (e.key === 'Escape') { e.stopPropagation(); setRenaming(null); } }} />
                  ) : (
                    <>
                      <b title={set.title}>{set.title}</b>
                      <button type="button" className="stn-icon-btn" onClick={() => setRenaming(set.title)} title={t('common:scol.rename')} aria-label={t('common:scol.rename')}><FiEdit2 size={12} /></button>
                    </>
                  )}
                  meta={`${set.connName || t('common:scol.noConnection')} · ${t(`common:scol.status.${set.status}`, { defaultValue: set.status })}`}
                  dirty={step === 'verify' && itemDirty}
                  onSave={step === 'verify' && item ? saveItem : null} saveDisabled={!itemDirty} saveLabel={t('common:scol.saveItem')}
                  onDelete={removeSet} />
                <WorkflowRibbon tabs={TABS} step={step} onStep={(s) => { setStep(s); setItem(null); }} label={s => t(`common:scol.steps.${s}`)}
                  suffix={(s) => {
                    if (s === 'collect' && sm.items) return ` (${fmt(sm.items)})`;
                    if (s === 'cleanse' && set.cleanse) return set.cleanse.stale ? ' !' : ` (${fmt(sm.kept)})`;
                    if (s === 'redact' && set.redact) return set.redact.off ? ' -' : (set.redact.stale ? ' !' : ' ✓');
                    if (s === 'verify' && sm.issues) return ` (${fmt(sm.issues)})`;
                    if (s === 'save' && set.status === 'done') return ' ✓';
                    return '';
                  }} />
                {step !== 'history' && <div className="srv-stepHelp" key={`help-${step}`}>{t(`common:scol.stepHelp.${step}`)}</div>}
                <div className="stn-step-wrap" key={`${set.id}-${step}`}>{body}</div>
              </>
            )}
          </>}
        </section>
      </div>
    </AppModal>
  );
};

SqlCollectModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  connections: PropTypes.arrayOf(PropTypes.object),
  defaultConnectionId: PropTypes.string,
  /** 편집기 새 탭으로 (sql) */
  onOpenInEditor: PropTypes.func,
  restore: PropTypes.shape({ id: PropTypes.string, step: PropTypes.string }),
  onView: PropTypes.func,
};

export default memoWindow(SqlCollectModal);
